import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getToken, logout, getUserId } from "../utils/auth";
import swal from "sweetalert";
import "./MCQTestPage.css";

/* ================= SHUFFLE UTILITY ================= */
const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

export default function MCQTestPage() {
  const { mcqId } = useParams();
  const navigate = useNavigate();
  const userId = getUserId();

  const [mcq, setMcq] = useState(null);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [started, setStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [totalTimeLeft, setTotalTimeLeft] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(() =>
    typeof document !== "undefined" ? !!document.fullscreenElement : false
  );
  const [fullscreenExitCount, setFullscreenExitCount] = useState(0);

  const totalTimerRef = useRef(null);
  const tabSwitchCountRef = useRef(0);

  const STORAGE_KEY = useRef(`mcq_${mcqId}_${userId}_answers`).current;
  const ORDER_KEY = useRef(`mcq_${mcqId}_${userId}_order`).current;
  console.log(score);

  /* ---------- SUBMIT TEST ---------- */
  const submitTest = useCallback(
    async (forced = false) => {
      if (submitting || !mcq) return;

      /* === MANUAL SUBMISSION CONFIRMATION PROMPT === */
      if (!forced) {
        const totalQuestions = mcq.questions.length;
        const answeredCount = mcq.questions.filter((q) => answers[q._id]).length;
        
        // Find exactly which question numbers (1-indexed) were skipped
        const skippedNumbers = mcq.questions
          .map((q, idx) => (answers[q._id] ? null : idx + 1))
          .filter(Boolean);

        const confirmSubmit = await swal({
          title: "Are you sure you want to submit?",
          text: `Answered: ${answeredCount}\nSkipped: ${totalQuestions - answeredCount}\n${
            skippedNumbers.length > 0 ? `Skipped Question Numbers: ${skippedNumbers.join(", ")}` : "All questions answered!"
          }\n\nOnce submitted, you cannot modify your choices!`,
          icon: "warning",
          buttons: ["Cancel", "Yes, Submit"],
          dangerMode: true,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });

        if (!confirmSubmit) return;
      }

      setSubmitting(true);
      try {
        let totalScore = 0;
        let technicalScore = 0;
        let verbalScore = 0;
        let aptitudeScore = 0;
        let totalPossibleMarks = 0;

        const payload = mcq.questions.map((q) => {
          const selectedOption = answers[q._id] || null;
          const isCorrect = selectedOption === q.correctOption;
          const questionMark = q.mark || 1;

          totalPossibleMarks += questionMark;

          let marksObtained = 0;
          if (isCorrect) {
            marksObtained = questionMark;
            totalScore += questionMark;

            if (q.category === "Technical") {
              technicalScore += questionMark;
            } else if (q.category === "Verbal") {
              verbalScore += questionMark;
            } else if (q.category === "Aptitude") {
              aptitudeScore += questionMark;
            }
          };

          return {
            questionId: q._id,
            selectedOption: selectedOption,
            isCorrect: isCorrect,
            marksObtained: marksObtained,
          };
        });

        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/mcq-subissions/submit`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getToken()}`,
            },
            body: JSON.stringify({
              mcqId,
              studentId: userId,
              answers: payload,
              forcedSubmit: forced,
              scoresBreakdown: {
                totalScore,
                technicalScore,
                verbalScore,
                aptitudeScore,
                totalPossibleMarks,
              },
            }),
          }
        );

        const data = await res.json();
        console.log(data);

        setScore(totalScore);

        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(ORDER_KEY);

        if (forced) {
          await swal({
            title: "Test Auto Submitted",
            text: "Your test was auto-submitted due to a policy violation.",
            icon: "warning",
            button: "OK",
            closeOnClickOutside: false,
            closeOnEsc: false,
          });
        } else {
          await swal({
            title: "Test Completed",
            text: `MCQ submitted successfully!\n\nClick below to continue.`,
            icon: "success",
            button: mcq.isAssignmentPresent ? "Go to Assignment" : "Back to MCQs",
            closeOnClickOutside: false,
            closeOnEsc: false,
          });
        }

        if (mcq.isAssignmentPresent && mcq.assignmentId) {
          navigate(`/assignments/solve/${mcq.assignmentId}`);
        } else {
          navigate("/mcqs/student");
        }
      } catch (error) {
        console.error("Error submitting test summary calculation: ", error);
      } finally {
        exitFullscreenSafely();
      }
    },
    [mcqId, userId, answers, mcq, submitting, STORAGE_KEY, ORDER_KEY, navigate]
  );

  /* ---------- TIMER ---------- */
  useEffect(() => {
    if (!started || !mcq) return;

    if (totalTimeLeft === 0) {
      setTotalTimeLeft(mcq.duration * 60);
      return;
    }

    totalTimerRef.current = setInterval(() => {
      setTotalTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(totalTimerRef.current);
          submitTest(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(totalTimerRef.current);
  }, [started, totalTimeLeft, mcq, submitTest]);

  /* ---------- FULLSCREEN ---------- */
  const enterFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
    }
  };

  const exitFullscreenSafely = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  };

  useEffect(() => {
    const handleFullscreenChange = async () => {
      const inFullscreen = !!document.fullscreenElement;
      setIsFullscreen(inFullscreen);

      if (!inFullscreen) {
        const newCount = fullscreenExitCount + 1;
        setFullscreenExitCount(newCount);

        if (newCount === 1) {
          await swal({
            title: "Warning: Full Screen Exited",
            text: "You must remain in full screen mode.",
            icon: "warning",
            button: "OK",
          });
        } else if (newCount >= 2) {
          submitTest(true);
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [fullscreenExitCount, submitTest]);

  /* ---------- FETCH MCQ ---------- */
  useEffect(() => {
    const fetchMCQ = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/mcqs/${mcqId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (res.status === 401 || res.status === 403) {
        logout();
        navigate("/login");
        return;
      }

      const data = await res.json();
      console.log(data);

      const savedOrder = localStorage.getItem(ORDER_KEY);
      let finalQuestions;

      if (savedOrder) {
        const order = JSON.parse(savedOrder);
        finalQuestions = order
          .map((id) => data.questions.find((q) => q._id === id))
          .filter(Boolean);
      } else {
        const technicalGroup = [];
        const aptitudeGroup = [];
        const verbalGroup = [];
        const uncategorizedGroup = [];

        data.questions.forEach((q) => {
          const category = q.category ? q.category.trim() : "";
          if (category === "Technical") {
            technicalGroup.push(q);
          } else if (category === "Aptitude") {
            aptitudeGroup.push(q);
          } else if (category === "Verbal") {
            verbalGroup.push(q);
          } else {
            uncategorizedGroup.push(q);
          }
        });

        const shuffledTechnical = shuffleArray(technicalGroup);
        const shuffledAptitude = shuffleArray(aptitudeGroup);
        const shuffledVerbal = shuffleArray(verbalGroup);
        const shuffledUncategorized = shuffleArray(uncategorizedGroup);

        finalQuestions = [
          ...shuffledTechnical,
          ...shuffledAptitude,
          ...shuffledVerbal,
          ...shuffledUncategorized,
        ];

        localStorage.setItem(
          ORDER_KEY,
          JSON.stringify(finalQuestions.map((q) => q._id))
        );
      }

      setMcq({ ...data, questions: finalQuestions });

      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setAnswers(JSON.parse(saved));
    };

    fetchMCQ();
  }, [mcqId, navigate, STORAGE_KEY, ORDER_KEY]);

  /* ---------- TAB SWITCH DETECTION ---------- */
  useEffect(() => {
    if (!started) return;

    const handleViolation = async () => {
      tabSwitchCountRef.current += 1;

      if (tabSwitchCountRef.current === 2) {
        await swal({
          title: "Warning ⚠️",
          text: "Do not switch tabs again.",
          icon: "warning",
          button: "Continue Test",
        });
      }

      if (tabSwitchCountRef.current >= 3) {
        submitTest(true);
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) handleViolation();
    };

    const onBlur = () => handleViolation();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onBlur);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onBlur);
    };
  }, [started, submitTest]);

  /* ---------- START TEST ---------- */
  const startTest = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/mcq-submissions/student/${userId}/mcq/${mcqId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      const data = await res.json();

      if (data && data[0]) {
        await swal({
          title: "Test Already Attempted",
          text: "You have already submitted this test.",
          icon: "warning",
          button: "Back to MCQs",
        });

        navigate("/mcqs/student");
        return;
      }

      await enterFullscreen();
      setStarted(true);
    } catch (error) {
      console.error("Error checking submission:", error);
    }
  };

  if (!mcq) return null;

  if (!started) {
    const totalMarks = mcq.questions.reduce((sum, q) => sum + (q.mark || 1), 0);
    const categories = [...new Set(mcq.questions.map((q) => q.category).filter(Boolean))];

    return (
      <div className="start-screen">
        {/* Background grid */}
        <div className="start-bg-grid" />

        <div className="start-layout">
          {/* ── LEFT PANEL ── */}
          <div className="start-left">
            <div className="start-brand">TSS Student&apos;s Hub</div>
            <h1 className="start-title">{mcq.topic || mcq.title || "MCQ Assessment"}</h1>
            <p className="start-subtitle">Read all instructions carefully before beginning the test.</p>

            {/* Stats row */}
            <div className="start-stats">
              <div className="stat-box">
                <span className="stat-value">{mcq.questions.length}</span>
                <span className="stat-label">Questions</span>
              </div>
              <div className="stat-divider" />
              <div className="stat-box">
                <span className="stat-value">{mcq.duration}</span>
                <span className="stat-label">Minutes</span>
              </div>
              <div className="stat-divider" />
              <div className="stat-box">
                <span className="stat-value">{totalMarks}</span>
                <span className="stat-label">Total Marks</span>
              </div>
            </div>

            {/* Category tags */}
            {categories.length > 0 && (
              <div className="start-categories">
                {categories.map((cat) => (
                  <span key={cat} className="cat-tag">{cat}</span>
                ))}
              </div>
            )}

            {/* CTA */}
            <button className="start-btn" onClick={startTest}>
              <span>Begin Assessment</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
            <p className="start-ack">By starting, you agree to all exam rules and policies.</p>
          </div>

          {/* ── RIGHT PANEL ── */}
          <div className="start-right">
            <p className="rules-heading">Exam Instructions</p>

            <div className="rules-list">
              <div className="rule-row">
                <div className="rule-icon-wrap rule-blue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                </div>
                <div className="rule-text">
                  <strong>Fullscreen Mode</strong>
                  <p>The test runs in fullscreen. Exiting fullscreen a second time will auto-submit your test immediately.</p>
                </div>
              </div>

              <div className="rule-row">
                <div className="rule-icon-wrap rule-amber">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
                <div className="rule-text">
                  <strong>Tab Switching Monitored</strong>
                  <p>Switching tabs or windows is tracked. After 2 violations your test will be force-submitted.</p>
                </div>
              </div>

              <div className="rule-row">
                <div className="rule-icon-wrap rule-green">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <div className="rule-text">
                  <strong>Timed — {mcq.duration} Minutes</strong>
                  <p>The timer starts the moment you click Begin. The test auto-submits when time expires.</p>
                </div>
              </div>

              <div className="rule-row">
                <div className="rule-icon-wrap rule-purple">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </div>
                <div className="rule-text">
                  <strong>One Attempt Only</strong>
                  <p>Once an option is selected it is locked. You cannot revise your answer for any question.</p>
                </div>
              </div>

              <div className="rule-row">
                <div className="rule-icon-wrap rule-teal">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                </div>
                <div className="rule-text">
                  <strong>Auto-Saved Answers</strong>
                  <p>Every answer is saved instantly. Navigate freely between questions before final submission.</p>
                </div>
              </div>

              <div className="rule-row">
                <div className="rule-icon-wrap rule-red">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                </div>
                <div className="rule-text">
                  <strong>No External Resources</strong>
                  <p>Use of search engines, notes, or any outside assistance is strictly prohibited.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const question = mcq.questions[index];
  const selected = answers[question._id];
  const progress = ((index + 1) / mcq.questions.length) * 100;

  const selectOption = (opt) => {
    if (selected) return; 
    const updated = { ...answers, [question._id]: opt };
    setAnswers(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const splitQuestionText = (text) => {
    if (!text) return { prompt: "", code: "" };

    const normalized = String(text).replace(/\r\n/g, "\n");
    const lines = normalized.split("\n");

    if (lines.length > 1) {
      const codeLinePattern = /^\s*(for|while|if|switch|return|console\.log|printf|System\.out|def |class |import |const |let |var |int |float |double |public |private |protected |#include|using\s+namespace|function|=>|\/\*|\/\/|\{|\}|\)|\(|\[|\]|;)/i;

      const firstCodeIndex = lines.findIndex((line) => {
        const trimmed = line.trim();
        return (
          trimmed &&
          (codeLinePattern.test(trimmed) || /;\s*$/.test(trimmed) || /\{\s*$/.test(trimmed))
        );
      });

      if (firstCodeIndex > 0) {
        const prompt = lines.slice(0, firstCodeIndex).join("\n").trim();
        const code = lines.slice(firstCodeIndex).join("\n").trim();
        return { prompt, code };
      }

      const blankLineIndex = lines.findIndex((line) => line.trim() === "");
      if (blankLineIndex > 0 && blankLineIndex < lines.length - 1) {
        const prompt = lines.slice(0, blankLineIndex).join("\n").trim();
        const code = lines.slice(blankLineIndex + 1).join("\n").trim();
        return { prompt, code };
      }
    }

    const inlineCodePattern = /(for\s*\(|while\s*\(|switch\s*\(|if\s*\(|return\s+|console\.log\s*\(|printf\s*\(|System\.out\s*\(|def\s+|class\s+|import\s+|const\s+|let\s+|var\s+|int\s+main|#include\s+|using\s+namespace|function\s*\(|=>)/i;
    const inlineMatch = normalized.match(inlineCodePattern);

    if (inlineMatch) {
      const splitIndex = inlineMatch.index;
      const prompt = normalized.slice(0, splitIndex).trim();
      let code = normalized.slice(splitIndex).trim();

      if (code.endsWith("?")) {
        code = code.slice(0, -1).trim();
      }

      return { prompt, code };
    }

    const hasCodeIndicators = /\{|\}|;|printf|console\.log|System\.out|#include|=>/.test(normalized);
    if (hasCodeIndicators) {
      return { prompt: "", code: normalized.trim() };
    }

    return { prompt: normalized.trim(), code: "" };
  };

  const { prompt: questionPrompt, code: questionCode } = splitQuestionText(question.question);

  return (
    <div className="exam-wrapper">
      <div className={`exam-content ${!isFullscreen ? "blur" : ""}`}>


        <div className="progress-bar">
          <div style={{ width: `${progress}%` }} />
        </div>

        <div className="total-time-top-right">⏱️ {formatTime(totalTimeLeft)}</div>

        <div className="exam-container">
          <div className="question-panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span className="category-badge">{question.category}</span>
              {/* --- DYNAMIC NUMBERING DISPLAY FOR CURRENT STUDENT --- */}
              <span className="question-number-badge" style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                Question {index + 1} of {mcq.questions.length}
              </span>
            </div>
            
            <div className="question-text" style={{ marginTop: "15px" }}>
              {/* Prefixed question text with sequential numbers */}
              {questionPrompt ? <h2>Q{index + 1}. {questionPrompt}</h2> : null}
              {questionCode ? (
                <pre className="question-code-block">{questionCode}</pre>
              ) : (
                !questionPrompt && <h2>Q{index + 1}. {question.question}</h2>
              )}
            </div>
          </div>

          <div className="options-panel">
            {["A", "B", "C", "D"].map((opt) => (
              <div
                key={opt}
                className={`option-tile ${selected === opt ? "selected" : ""} ${selected ? "locked" : ""}`}
                onClick={() => selectOption(opt)}
              >
                <span className="opt-circle">{opt}</span>
                <span>{question[`option${opt}`]}</span>
              </div>
            ))}

            <div className="nav-buttons">
              {index > 0 && (
                <button className="prev-btn" onClick={() => setIndex((i) => i - 1)}>
                  ← Previous
                </button>
              )}

              {index < mcq.questions.length - 1 ? (
                <button onClick={() => setIndex((i) => i + 1)}>
                  Next →
                </button>
              ) : (
                <button
                  className="submit"
                  disabled={submitting}
                  onClick={() => submitTest(false)}
                >
                  Submit Test
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isFullscreen && fullscreenExitCount < 2 && (
        <div className="fullscreen-overlay">
          <button className="fullscreen-btn" onClick={enterFullscreen}>
            Enter Full Screen to Continue Test
          </button>
        </div>
      )}
    </div>
  );
}